import { useTranslation } from 'core/i18n/i18n';
import { showErrorNotification } from 'core/utils/notification-service';
import { settingsModulePaths } from 'core/routes/routes';
import { useCallback, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMaterialDefinitionById } from 'modules/settings/pods/settings-materials/service/material-definitions/hooks/use-material-definition-by-id.hook';
import { mapMaterialDefinitionToFormState } from 'modules/settings/pods/settings-materials/service/material-definitions/material-definitions.mapper';
import { useReferenceData } from 'modules/settings/pods/settings-materials/service/reference-data/use-reference-data.hook';
import { useMaterialDefinitionDataHandler } from './use-material-definition-data-handler.hook';
import { useMaterialDefinitionSaver } from './use-material-definition-saver.hook';
import { getTestConfigsError, validateQcTargetValues } from '../settings-material-definitions-form.validation';

const CREATE_MODE_PARAM = 'new';
const FETCH_ERROR_KEY = 'settings.materials.material-definitions.form.notifications.fetch-error';
const HEADER_TITLE_CREATE_KEY = 'settings.materials.material-definitions.form.header-title';
const HEADER_TITLE_EDIT_KEY = 'settings.materials.material-definitions.form.header-title-edit';

export const useMaterialDefinitionForm = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { materialDefinitionId } = useParams<{ materialDefinitionId: string }>();
    const isEditMode = Boolean(materialDefinitionId) && materialDefinitionId !== CREATE_MODE_PARAM;

    const handleClose = useCallback(() => {
        navigate(settingsModulePaths.materialDefinitions);
    }, [navigate]);

    const {
        material,
        isLoading: isFetchingMaterial,
        isError: isMaterialFetchError,
    } = useMaterialDefinitionById(materialDefinitionId, isEditMode);

    useEffect(() => {
        if (isMaterialFetchError) {
            showErrorNotification(t(FETCH_ERROR_KEY));
            handleClose();
        }
    }, [isMaterialFetchError, handleClose, t]);

    const initialData = useMemo(() => (material ? mapMaterialDefinitionToFormState(material) : null), [material]);

    const { deviceTypes, tests, isLoading: isLoadingRefData } = useReferenceData();

    const defaultLevelName = t('settings.materials.material-definitions.form.qc-config.levels.default-name', { n: 1 });
    const {
        formState,
        errors,
        validate,
        isDataValid,
        unsavedData,
        handleDataChange,
        handleAddTest,
        handleRemoveTest,
        handleTestChange,
        handleResultTypeChange,
        handlePossibleValuesChange,
        handleRangeModeChange,
        handleAddLevel,
        handleRemoveLevel,
        handleLevelChange,
        handleValidity,
    } = useMaterialDefinitionDataHandler({ onClose: handleClose, initialData, defaultLevelName });

    const saver = useMaterialDefinitionSaver({
        onSuccess: () => {
            unsavedData.setIsUnsavedChanges(false);
            handleClose();
        },
    });

    const handleSave = () => {
        if (!validate()) {
            return;
        }
        if (isEditMode && materialDefinitionId) {
            saver.update(materialDefinitionId, formState);
        } else {
            saver.create(formState);
        }
    };

    const handleCancel = () => unsavedData.showUnsavedChangesIfNeededOrClose();

    const isLoading = isLoadingRefData || isFetchingMaterial;
    const headerTitleKey = isEditMode ? HEADER_TITLE_EDIT_KEY : HEADER_TITLE_CREATE_KEY;
    const hasLots = material?.hasLots ?? false;
    const readOnlyLotRules = isEditMode && hasLots;

    const testConfigsError = formState.testConfigs?.length ? getTestConfigsError(formState.testConfigs) : undefined;
    const targetErrors = formState.testConfigs?.length ? validateQcTargetValues(formState.testConfigs) : {};

    return {
        formState,
        errors,
        testConfigsError,
        targetErrors,
        deviceTypes,
        tests,
        isLoading,
        readOnlyLotRules,
        handleAddTest,
        handleRemoveTest,
        handleTestChange,
        handleDataChange,
        handleResultTypeChange,
        handlePossibleValuesChange,
        handleRangeModeChange,
        handleAddLevel,
        handleRemoveLevel,
        handleLevelChange,
        handleValidity,
        handleSave,
        handleCancel,
        isDataValid,
        isSaving: saver.isSaving,
        headerTitleKey,
    };
};
